/** DECIMAL(28,6) — the scale both sides are padded to before comparing. */
const DECIMAL_SCALE = 6;

interface DecimalParts {
  negative: boolean;
  integer: string;
  fraction: string;
}

const parse = (value: string): DecimalParts => {
  const trimmed = value.trim();
  const signed = trimmed.startsWith("-") || trimmed.startsWith("+");
  const [integerPart, fractionPart = ""] = (
    signed ? trimmed.slice(1) : trimmed
  ).split(".");

  return {
    negative: trimmed.startsWith("-"),
    integer: integerPart.replace(/^0+/, ""),
    fraction: fractionPart.padEnd(DECIMAL_SCALE, "0"),
  };
};

const isZero = ({ integer, fraction }: DecimalParts): boolean =>
  integer === "" && /^0*$/.test(fraction);

const compareMagnitude = (
  left: DecimalParts,
  right: DecimalParts,
): -1 | 0 | 1 => {
  if (left.integer.length !== right.integer.length) {
    return left.integer.length > right.integer.length ? 1 : -1;
  }
  if (left.integer !== right.integer) {
    return left.integer > right.integer ? 1 : -1;
  }
  if (left.fraction !== right.fraction) {
    return left.fraction > right.fraction ? 1 : -1;
  }
  return 0;
};

// Compared as text, never as a number: DECIMAL(28,6) exceeds `Number.MAX_SAFE_INTEGER`,
// so a numeric compare would round two genuinely different values together.
export const compareDecimalStrings = (a: string, b: string): -1 | 0 | 1 => {
  const left = parse(a);
  const right = parse(b);

  if (left.negative !== right.negative) {
    if (isZero(left) && isZero(right)) {
      return 0;
    }
    return left.negative ? -1 : 1;
  }

  const magnitude = compareMagnitude(left, right);
  if (magnitude === 0) {
    return 0;
  }
  if (left.negative) {
    return magnitude === 1 ? -1 : 1;
  }
  return magnitude;
};
